import { createApi } from '@reduxjs/toolkit/query/react';
import apiClient from '../../helpers/apiClient';

// Custom base query that uses our apiClient for consistent error handling
const customBaseQuery = async ({ url, method = 'GET', body, params }) => {
  try {
    // Check if this is a public request
    const isPublicRequest = params && params.publicAuth;
    
    const response = await apiClient({
      method,
      url,
      data: body,
      params: params,
      headers: isPublicRequest ? { 'X-Skip-Auth': true } : {}
    });
    
    // Check if apiClient returned an error (it resolves even for errors)
    if (!response.success && response.error) {
      return {
        error: {
          status: response.error.status || 500,
          data: {
            message: response.error.message,
            ...response.error
          }
        }
      };
    }
    
    return { data: response };
  } catch (error) {
    return {
      error: {
        status: error.response?.status || 500,
        data: error.response?.data || error.message
      }
    };
  }
};

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: customBaseQuery,
  tagTypes: ['SummaryReport', 'MetricReport', 'ReportDefinition'],
  endpoints: (builder) => ({
    // Get summary report for a slice
    getSummaryReport: builder.query({
      query: (arg) => {
        // Handle both simple sliceId string and object with params
        const sliceId = typeof arg === 'string' ? arg : arg.sliceId;
        const params = typeof arg === 'object' ? arg.params : undefined;

        return {
          url: `/v1/reports/summary/${sliceId}`,
          method: 'GET',
          params: params // Include query parameters if provided
        };
      },
      providesTags: (result, error, arg) => {
        const sliceId = typeof arg === 'string' ? arg : arg.sliceId;
        return [{ type: 'SummaryReport', id: sliceId }];
      },
      // Transform response to extract data
      transformResponse: (response) => response.data
    }),

    // Get detailed metric report
    getMetricReport: builder.query({
      query: ({ metric_id, slice_id, params }) => ({
        url: `/v1/reports/metric/${metric_id}/${slice_id}`,
        method: 'GET',
        params: params // Include query parameters if provided
      }),
      providesTags: (result, error, { metric_id, slice_id }) => [
        { type: 'MetricReport', id: `${metric_id}-${slice_id}` }
      ],
      // Transform response to extract data
      transformResponse: (response) => response.data
    }),

    // Create a new report definition
    createReportDefinition: builder.mutation({
      query: (data) => ({
        url: '/v1/reports/definitions',
        method: 'POST',
        // If data has expiresAt at root, separate it out; otherwise treat whole object as sliceData
        body: data.expiresAt !== undefined
          ? { sliceData: { ...data, expiresAt: undefined }, expiresAt: data.expiresAt }
          : { sliceData: data }
      }),
      // Transform response to extract data
      transformResponse: (response) => response.data
    }),

    // Get report definition by ID and token
    getReportDefinition: builder.query({
      query: ({ id, token }) => ({
        url: `/v1/reports/definitions/${id}/${token}`,
        method: 'GET'
      }),
      providesTags: (result, error, { id, token }) => [
        { type: 'ReportDefinition', id: `${id}-${token}` }
      ],
      // Transform response to extract data
      transformResponse: (response) => response.data
    }),

    // Public access is handled via compressed URL data in frontend
    // No backend API calls needed for public reports
  })
});

// Export hooks for usage in functional components
export const {
  useGetSummaryReportQuery,
  useGetMetricReportQuery,
  useCreateReportDefinitionMutation,
  useGetReportDefinitionQuery
} = reportsApi;